import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Loader2, Send, Wallet } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FIN_ROLES } from "@/lib/stages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { StatusPill, money } from "@/components/finance/shared";

const cls = "bg-slate-950 border-slate-700";
const NEW = { description: "", amount: "" };

export function CashAdvancePanel({ sh, canEdit }) {
  const { user } = useAuth();
  const isFinance = FIN_ROLES.includes(user?.role);
  const [list, setList] = useState([]);
  const [items, setItems] = useState([{ ...NEW }]);
  const [neededBy, setNeededBy] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/shipments/${sh.id}/cash-advances`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sh.id]);
  const total = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const upd = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));

  const request = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/shipments/${sh.id}/cash-advance`, {
        needed_by: neededBy, items: items.map((i) => ({ description: i.description, amount: parseFloat(i.amount) || 0 })),
      });
      toast.success(`${data.number} sent to Finance for approval`);
      setItems([{ ...NEW }]); setNeededBy(""); load();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="cash-advance-panel" className="mt-8 border-t border-slate-800 pt-6">
      <div className="flex items-center gap-2 font-display text-lg font-bold mb-1"><Wallet size={16} className="text-sky-400" /> Cash advance</div>
      <p className="text-xs text-slate-500 mb-4">Requests go to Finance → Request & Voucher (Operational Fund) with this shipment number. Requests made in Finance for this shipment also show here.</p>
      {canEdit && (
        <div className="bg-slate-950/40 border border-slate-800 rounded-md p-4 space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex flex-wrap sm:flex-nowrap gap-2">
              <Input data-testid={`ca-desc-${i}`} value={it.description} onChange={(e) => upd(i, "description", e.target.value)} placeholder="e.g. Arrastre, Brokerage, Wharfage" className={`w-full sm:flex-1 ${cls}`} />
              <Input data-testid={`ca-amount-${i}`} type="number" min="0" value={it.amount} onChange={(e) => upd(i, "amount", e.target.value)} placeholder="0.00" className={`w-32 text-right font-mono-ops ${cls}`} />
              <Button data-testid={`ca-remove-${i}`} size="sm" variant="ghost" disabled={items.length === 1} onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="text-rose-300 hover:bg-rose-500/10"><Trash2 size={14} /></Button>
            </div>
          ))}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 pt-2">
            <div className="flex flex-wrap items-end gap-3">
              <Button data-testid="button-add-ca-line" size="sm" variant="outline" onClick={() => setItems([...items, { ...NEW }])} className="border-slate-700 hover:bg-slate-800"><Plus size={14} className="mr-1" /> Add line</Button>
              <div><Label className="text-xs">Needed by</Label><Input data-testid="input-ca-needed-by" type="date" value={neededBy} onChange={(e) => setNeededBy(e.target.value)} className={cls} /></div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono-ops text-sm">Total <span data-testid="ca-total" className="text-sky-300">{money(total)}</span></span>
              <Button data-testid="button-request-cash-advance" onClick={request} disabled={busy || total <= 0} className="bg-blue-600 hover:bg-blue-500">
                {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />} Request to Finance
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="mt-4 space-y-2">
        {list.length === 0 && <div className="text-xs text-slate-500">No cash advance requests yet.</div>}
        {list.map((r) => (
          <div key={r.id} data-testid={`ca-request-${r.number}`} className="flex flex-wrap items-start justify-between gap-3 bg-slate-950/60 border border-slate-800 rounded-md px-4 py-2.5 text-sm">
            <div>
              <span className="font-mono-ops text-xs text-slate-300">{r.number}</span>
              <span className="text-slate-500 text-xs"> · {r.created_by_name} · {r.source === "release" ? "from Release" : "from Finance"}</span>
              {r.decision?.remarks && <div className="text-xs text-slate-500">{r.decision.by_name}: {r.decision.remarks}</div>}
              {r.items?.length > 0 && (
                <div data-testid={`ca-budget-${r.number}`} className="mt-1.5 space-y-0.5">
                  {r.items.map((it, i) => (
                    <div key={i} className="flex justify-between gap-6 text-xs text-slate-400 max-w-md">
                      <span>{it.description}</span><span className="font-mono-ops">{money(it.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              {r.liquidation && (
                <div data-testid={`ca-liquidation-${r.number}`} className="text-xs text-slate-400 mt-1">
                  Liquidation <span className="font-mono-ops">{r.liquidation.number}</span> · {r.liquidation.status} · Receipted {money(r.liquidation.receipted_total)} · Unreceipted {money(r.liquidation.unreceipted_total)} · Balance <span className={r.liquidation.variance < 0 ? "text-rose-300" : "text-emerald-300"}>{money(r.liquidation.variance)}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono-ops">{money(r.amount)}</span><StatusPill status={r.status} />
              {r.status === "Approved" && !r.liquidation && (isFinance
                ? <Link data-testid={`ca-liquidate-${r.number}`} to={`/finance?tab=liquidations&fund=${r.id}`} className="text-xs text-sky-300 hover:text-sky-200 underline underline-offset-4">Liquidate</Link>
                : <span className="text-[10px] uppercase tracking-widest text-amber-300">Awaiting liquidation</span>)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
